// 首页 Hero 文案块解析
import { isHeroRowFitMetaEntry } from '@/components/home/utils/heroRowFitMeta'

/** 解析百分比字符串为 0–100 的数值 */
export function parseHeroPercent(val, fallback = 50) {
  if (val == null || val === '') return fallback
  const n = parseFloat(String(val).replace('%', ''))
  return Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : fallback
}

/** 根据文字水平对齐方式计算 CSS transform 偏移 */
export function heroTextTransform(textAlign = 'center') {
  switch (textAlign) {
    case 'left':
      return 'translate(0, -50%)'
    case 'right':
      return 'translate(-100%, -50%)'
    default:
      return 'translate(-50%, -50%)'
  }
}

/** 将 hero 对齐方式映射为 CSS text-align 值 */
export function heroTextContentAlign(textAlign = 'center') {
  switch (textAlign) {
    case 'right':
      return 'right'
    default:
      return 'left'
  }
}

/** 根据文字位置与对齐方式计算最大宽度百分比，避免溢出屏幕 */
export function computeHeroTextMaxWidth(textAlign = 'center', posX = '50%', marginPercent = 2) {
  const x = parseHeroPercent(posX)
  const margin = Math.max(0, marginPercent)
  let maxW
  switch (textAlign) {
    case 'left':
      maxW = 100 - x - margin
      break
    case 'right':
      maxW = x - margin
      break
    default:
      maxW = 2 * Math.min(x, 100 - x) - margin
      break
  }
  return `${Math.max(8, Math.round(maxW * 10) / 10)}%`
}

/** 按缩放比例调整字号，最小 10px */
export function scaleFontSize(fontSize, scale) {
  const match = String(fontSize || '24px').match(/^([\d.]+)(px|rem|em)$/)
  if (!match) return fontSize || '24px'
  const value = Math.max(10, Math.round(parseFloat(match[1]) * scale * 100) / 100)
  return `${value}${match[2]}`
}

/** 将 hero 文字配置转为内联样式对象 */
export function heroTextStyle(text, scale = 1) {
  const textAlign = text.textAlign || 'center'
  const hasManualBreaks = /[\r\n]/.test(String(text.text ?? ''))

  return {
    left: text.posX || '50%',
    top: text.posY || '50%',
    color: text.color || '#ffffff',
    fontSize: scaleFontSize(text.fontSize, scale),
    fontWeight: text.fontWeight || 'normal',
    fontFamily: text.fontFamily || undefined,
    textAlign: heroTextContentAlign(textAlign),
    transform: heroTextTransform(textAlign),
    whiteSpace: hasManualBreaks ? 'pre-wrap' : 'nowrap',
    wordBreak: hasManualBreaks ? 'break-word' : 'normal',
    maxWidth: computeHeroTextMaxWidth(textAlign, text.posX)
  }
}

/** 过滤掉隐藏元数据条目和空文本，返回可见 hero 文字列表 */
export function filterVisibleTexts(texts = []) {
  return (texts || []).filter(
    (entry) => !isHeroRowFitMetaEntry(entry) && String(entry.text || '').trim()
  )
}

/** 过滤空文案或空链接的海报按钮 */
export function filterVisibleButtons(buttons = []) {
  return (buttons || []).filter(
    (entry) => String(entry?.text || '').trim() && String(entry?.linkUrl || '').trim()
  )
}

/** 将 hero 按钮配置转为定位样式，配色走 CSS 变量 */
export function heroButtonStyle(button) {
  const textAlign = button?.textAlign || 'center'
  return {
    left: button?.posX || '50%',
    top: button?.posY || '72%',
    transform: heroTextTransform(textAlign),
    '--hero-btn-fg': button?.color || '#ffffff',
    '--hero-btn-bg': button?.backgroundColor || '#d40000'
  }
}

export function isExternalHeroLink(url) {
  return /^https?:\/\//i.test(String(url || '').trim())
}

export function isInternalHeroLink(url) {
  const value = String(url || '').trim()
  return value.startsWith('/') && !value.startsWith('//')
}

export function resolveHeroButtonHref(url) {
  const value = String(url || '').trim()
  if (!value) return ''
  if (isExternalHeroLink(value) || isInternalHeroLink(value)) return value
  return `/${value.replace(/^\/+/, '')}`
}
